import { tempoApi } from '@/lib/api/tempoApi';
import type {
  BacklogItem,
  BacklogItemBulkDeleteCommitRequest,
  BacklogItemBulkDeleteStageRequest,
  BacklogItemBulkEditCommitRequest,
  BacklogItemBulkEditStageRequest,
  BacklogItemDataSet,
  BacklogItemPatchRequest,
  BacklogItemSelectionCommitRequest,
  BacklogItemSelectionResponse,
  BacklogItemSelectionStageRequest,
  BulkItemProxyResponse,
  ItemProxyResponse,
} from '@/lib/api/types/adaptive-planner/backlog-item';

type BacklogItemParams = { planId: number };

export type BacklogItemFilters = {
  teamIds?: number[];
  estimationIds?: number[];
  itemTypeIds?: number[];
  itemStatusIds?: number[];
  needsTeamOrEstimate?: boolean;
};

const buildBacklogItemQueryParams = (filters?: BacklogItemFilters): string => {
  if (!filters) return '';

  const params = new URLSearchParams();

  if (filters.teamIds && filters.teamIds.length > 0) {
    params.set('teamIds', filters.teamIds.join(','));
  }
  if (filters.estimationIds && filters.estimationIds.length > 0) {
    params.set('estimationIds', filters.estimationIds.join(','));
  }
  if (filters.itemTypeIds && filters.itemTypeIds.length > 0) {
    params.set('itemTypeIds', filters.itemTypeIds.join(','));
  }
  if (filters.itemStatusIds && filters.itemStatusIds.length > 0) {
    params.set('itemStatusIds', filters.itemStatusIds.join(','));
  }
  if (filters.needsTeamOrEstimate) {
    params.set('needsTeamOrEstimate', 'true');
  }

  return params.toString() ? `?${params.toString()}` : '';
};

export const bulkFetchBacklogItems = async ({
  planId,
  itemIds,
}: BacklogItemParams & { itemIds: number[] }) => {
  const response = await tempoApi()
    .post(`api/v1/adaptive-planner/plans/${planId}/backlog-items/bulk-fetch`, {
      json: itemIds,
    })
    .json<BacklogItem[]>();
  return response;
};

const fetchBacklogItemsDataSet = async ({
  planId,
  filters,
}: BacklogItemParams & { filters?: BacklogItemFilters }) => {
  const queryParams = buildBacklogItemQueryParams(filters);
  const response = await tempoApi()
    .get(`api/v1/adaptive-planner/plans/${planId}/backlog-items/data-set${queryParams}`)
    .json<BacklogItemDataSet[]>();
  return response;
};

const deleteBacklogItem = async ({
  itemStoreId,
  planId,
}: BacklogItemParams & { itemStoreId: number }) => {
  return tempoApi().delete(`api/v1/adaptive-planner/plans/${planId}/backlog-items/${itemStoreId}`);
};

const updateBacklogItem = async ({
  itemStoreId,
  planId,
  data,
}: BacklogItemParams & {
  itemStoreId: number;
  data: BacklogItemPatchRequest;
}) => {
  const response = await tempoApi()
    .patch(`api/v1/adaptive-planner/plans/${planId}/backlog-items/${itemStoreId}`, {
      json: data,
    })
    .json<BacklogItem>();
  return response;
};

const fetchBacklogItemsAssignedTeamIds = async ({ planId }: BacklogItemParams) => {
  const response = await tempoApi()
    .get(`api/v1/adaptive-planner/plans/${planId}/backlog-items/assigned-team-ids`)
    .json<number[]>();
  return response;
};

const stageSelection = async ({
  planId,
  sessionId,
  itemIds,
}: BacklogItemParams & BacklogItemSelectionStageRequest): Promise<void> => {
  await tempoApi().post(`api/v1/adaptive-planner/plans/${planId}/backlog-items/selection/stage`, {
    json: { sessionId, itemIds },
  });
};

const commitSelection = async ({
  planId,
  sessionId,
}: BacklogItemParams &
  BacklogItemSelectionCommitRequest): Promise<BacklogItemSelectionResponse> => {
  const response = await tempoApi()
    .post(`api/v1/adaptive-planner/plans/${planId}/backlog-items/selection/commit`, {
      json: { sessionId },
    })
    .json<BacklogItemSelectionResponse>();
  return response;
};

const fetchBacklogItemsCount = async ({
  planId,
  filters,
}: BacklogItemParams & { filters?: BacklogItemFilters }): Promise<number> => {
  const queryParams = buildBacklogItemQueryParams(filters);
  const response = await tempoApi()
    .get(`api/v1/adaptive-planner/plans/${planId}/backlog-items/count${queryParams}`)
    .json<number>();
  return response;
};

const bulkFetchItemsProxy = async ({
  planId,
  itemIds,
}: BacklogItemParams & { itemIds: number[] }): Promise<ItemProxyResponse[]> => {
  const response = await tempoApi()
    .post(`api/v1/adaptive-planner/plans/${planId}/items/bulk-fetch`, {
      json: itemIds,
    })
    .json<BulkItemProxyResponse>();
  return response.items;
};

const stageBulkEdit = async ({
  planId,
  sessionId,
  request,
}: BacklogItemParams & BacklogItemBulkEditStageRequest): Promise<void> => {
  await tempoApi().post(`api/v1/adaptive-planner/plans/${planId}/backlog-items/bulk-edit/stage`, {
    json: { sessionId, request },
  });
};

const commitBulkEdit = async ({
  planId,
  sessionId,
}: BacklogItemParams & BacklogItemBulkEditCommitRequest): Promise<BacklogItem[]> => {
  const response = await tempoApi()
    .post(`api/v1/adaptive-planner/plans/${planId}/backlog-items/bulk-edit/commit`, {
      json: { sessionId },
    })
    .json<BacklogItem[]>();
  return response;
};

const stageBulkDelete = async ({
  planId,
  sessionId,
  itemIds,
}: BacklogItemParams & BacklogItemBulkDeleteStageRequest): Promise<void> => {
  await tempoApi().post(`api/v1/adaptive-planner/plans/${planId}/backlog-items/bulk-delete/stage`, {
    json: { sessionId, itemIds },
  });
};

const commitBulkDelete = async ({
  planId,
  sessionId,
}: BacklogItemParams & BacklogItemBulkDeleteCommitRequest): Promise<void> => {
  await tempoApi().post(
    `api/v1/adaptive-planner/plans/${planId}/backlog-items/bulk-delete/commit`,
    {
      json: { sessionId },
    },
  );
};

export const AdaptivePlannerBacklogItemApi = {
  bulkFetchBacklogItems,
  bulkFetchItemsProxy,
  fetchBacklogItemsDataSet,
  fetchBacklogItemsAssignedTeamIds,
  deleteBacklogItem,
  updateBacklogItem,
  stageSelection,
  commitSelection,
  stageBulkEdit,
  commitBulkEdit,
  stageBulkDelete,
  commitBulkDelete,
  fetchBacklogItemsCount,
} as const;
